import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Container, Button, CircularProgress, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { AppContext } from "../Store";
import { IChild } from "../shared/types/child";
import ObservationsPrintView from "../shared/ObservationsPrintView";
import { filterReportObservations } from "../shared/reportObservations";
import { lastSeptember } from "../shared/reportDates";
import { generateProgressSummary, AI_MODEL_OPTIONS } from "../shared/aiSummary";
import { createReportDraft } from "../shared/reportDraft";
import { useSnackbar } from "../shared/SnackbarProvider";
import { getLocal, setLocal } from "../shared/localStore";
import EmailDraftModal, { defaultEmailTemplate, EmailDraftTemplate } from "../shared/EmailDraftModal";

const summaryKey = (childId: string, fromDate?: Date | null) =>
  `reportSummary:${childId}:${fromDate ? fromDate.getTime() : "all"}`;
// The email template is shared across children -- one default to edit once.
const EMAIL_TEMPLATE_KEY = "reportEmail";

const ObservationsPrintPage: React.FC = () => {
  const { childId } = useParams<{ childId: string }>();
  const context = useContext(AppContext);
  const { childrenData, userContext } = context;
  const snack = useSnackbar();
  const [child, setChild] = useState<IChild>();
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [summary, setSummary] = useState<string>("");
  const [summarising, setSummarising] = useState(false);
  const [aiModel, setAiModel] = useState<string>(AI_MODEL_OPTIONS[AI_MODEL_OPTIONS.length - 1].id);
  const [drafting, setDrafting] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState<EmailDraftTemplate>(defaultEmailTemplate());
  const reportRef = useRef<HTMLDivElement>(null);
  const lastSummaryKey = useRef<string | null>(null);

  useEffect(() => {
    if (!childrenData) return;
    const child = childrenData.find((child) => child.id === childId);
    setChild(child);
  }, [childrenData, childId]);

  // Restore the AI summary for this child + date range from this browser.
  useEffect(() => {
    if (!child) return;
    const saved = getLocal<string>(summaryKey(child.id, fromDate));
    setSummary(saved ?? "");
  }, [child, fromDate]);

  // Persist the AI summary as it is generated or edited. When the date range
  // changes the old summary belongs to the old range, so it is not re-saved
  // under the new key -- that keeps a stale summary from clobbering a saved one.
  useEffect(() => {
    if (!child) return;
    const key = summaryKey(child.id, fromDate);
    const previous = lastSummaryKey.current;
    lastSummaryKey.current = key;
    if (!summary) return;
    if (previous === null || previous === key) setLocal(key, summary);
  }, [child, fromDate, summary]);

  // Restore the shared email template from this browser.
  useEffect(() => {
    if (!child) return;
    const saved = getLocal<EmailDraftTemplate>(EMAIL_TEMPLATE_KEY);
    if (saved) setEmailTemplate(saved);
  }, [child]);

  // Persist the shared email template as it is edited.
  useEffect(() => {
    if (!child) return;
    setLocal(EMAIL_TEMPLATE_KEY, emailTemplate);
  }, [child, emailTemplate]);

  // The exact set of observations on the report, so the AI sees only those.
  const observations = useMemo(
    () => (child ? filterReportObservations(child.observations, fromDate) : []),
    [child, fromDate]
  );

  const handlePrint = () => {
    window.print();
  };

  const handleSummarise = async () => {
    if (!child) return;
    setSummarising(true);
    try {
      snack("Generating AI summary...", "info");
      const generated = await generateProgressSummary(child, observations, fromDate, aiModel);
      setSummary(generated);
      snack("AI summary generated. Review and edit it before printing.", "success");
    } catch (error: any) {
      console.error("Error generating AI summary", error);
      snack(error?.message || "Could not generate the AI summary.", "error");
    } finally {
      setSummarising(false);
    }
  };

  const handleEmailCreate = async () => {
    if (!child || !reportRef.current) return;
    setDrafting(true);
    try {
      snack("Generating PDF and creating draft...", "info");
      const firstName = child.registration.childFirstName;
      const substitute = (text: string) => text.replace(/<CHILD>/g, firstName);
      const result = await createReportDraft(
        child.id,
        reportRef.current,
        substitute(emailTemplate.subject),
        substitute(emailTemplate.body)
      );
      setEmailOpen(false);
      snack(
        `Draft for ${result.to} saved in info@bee-happy.club drafts (${Math.round(result.pdfBytes / 1024)}KB). Not sent.`,
        "success"
      );
    } catch (error: any) {
      console.error("Error creating report draft", error);
      snack(error?.message || "Could not create the email draft.", "error");
    } finally {
      setDrafting(false);
    }
  };

  return (
    <Container maxWidth="lg" style={{ minHeight: "100vh" }}>
      <div className="no-print" style={{ marginBottom: 16, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="From date"
            value={fromDate}
            onChange={(value) => setFromDate(value)}
            format="yyyy-MMM-dd"
            slotProps={{ textField: { size: "small" } }}
          />
        </LocalizationProvider>
        <Button variant="outlined" color="primary" onClick={() => setFromDate(lastSeptember())}>
          From September
        </Button>
        <Button variant="outlined" color="primary" onClick={() => setFromDate(null)} disabled={!fromDate}>
          All time
        </Button>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="ai-model-label">AI model</InputLabel>
          <Select
            labelId="ai-model-label"
            label="AI model"
            value={aiModel}
            onChange={(event) => setAiModel(event.target.value)}
          >
            {AI_MODEL_OPTIONS.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleSummarise}
          disabled={summarising || observations.length === 0}
          startIcon={summarising ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {summary ? "Regenerate AI Summary" : "AI Summary"}
        </Button>
        <Button variant="contained" color="primary" onClick={handlePrint}>
          Print
        </Button>
        {/* Drafts always land in info@bee-happy.club's mailbox, so only that
            account gets the button. The function enforces this too. */}
        {userContext?.isAdmin() && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setEmailOpen(true)}
            disabled={drafting || !child || observations.length === 0}
          >
            Email Draft to Parent
          </Button>
        )}
        {!child && (
          <Button variant="contained" color="primary">
            Go Back
          </Button>
        )}
      </div>
      {child && (
        <div ref={reportRef}>
          <ObservationsPrintView
            child={child}
            fromDate={fromDate}
            summary={summary}
            onSummaryChange={setSummary}
          />
        </div>
      )}
      <EmailDraftModal
        open={emailOpen}
        childName={child?.registration.childFirstName ?? ""}
        template={emailTemplate}
        onTemplateChange={setEmailTemplate}
        creating={drafting}
        onCancel={() => setEmailOpen(false)}
        onCreate={handleEmailCreate}
      />
    </Container>
  );
};

export default ObservationsPrintPage;
